import { FilterOutlined } from '@ant-design/icons'
import type { ViewConfigFilterNode, ViewConfigTreeNode } from '@salescore/core'
import { stableStringify } from '@salescore/features'
// eslint-disable-next-line sonarjs/deprecation
import { addModelIdProperty, useBooleanState } from '@salescore/frontend-common'
import { Col, Collapse, Row, Space, Switch } from 'antd'
import { t } from 'i18next'
import type { ReactElement } from 'react'
import { useSetRecoilState } from 'recoil'

import { convertToFilterNodeForForm } from '../../../../recoil/navigation/hooks'
import { kpiFormModifiedAtom } from '../../../../recoil/view/atoms'
import { useCurrentNodeProperties } from '../../../../recoil/view/hooks/kpiForm'
import { useModelSearcher } from '../../../../recoil/view/selectors/modelSearcherSelector'
import {
  applyAdvancedSettingsFlag,
  ViewFilterTreeForm,
} from '../../ViewUISheetWithNavigation/SheetNavigation/filter/ViewFilterTreeForm'

//
// extraSheetSetting.filterTreeのフォーム
// KPIをシートとして表示するときにだけ適用されるフィルタ
//

export const KpiFormExtraFilterTreeFormItem = ({
  currentSheetTree,
  filterTree,
  setFilterTree,
}: {
  currentSheetTree: ViewConfigTreeNode | undefined
  filterTree: ViewConfigFilterNode | undefined
  setFilterTree: (filterTree: ViewConfigFilterNode | undefined) => void
}): ReactElement => {
  const currentNodeProperties = useCurrentNodeProperties()
  const setFormModified = useSetRecoilState(kpiFormModifiedAtom)
  const modelSearcher = useModelSearcher()
  // eslint-disable-next-line sonarjs/deprecation
  const isAdvancedModeState = useBooleanState(
    (filterTree?.children ?? []).length > 0 || filterTree?.logicalOperator === 'or',
  )
  const onChange = (filterTree: ViewConfigFilterNode | undefined, option?: { asInitialization?: boolean }) => {
    setFilterTree(filterTree)
    // 変更を行ったら、変更フラグを立てる
    // フォームの初期レンダリング時に、一部ロジックでonChangeを呼ぶことがあるが、この際は変更フラグを立てない
    if (option?.asInitialization !== true) {
      setFormModified(true)
    }
  }

  const propertiesWithNode = currentNodeProperties.map((x) => ({
    node: x.node.node,
    model: x.model,
    property: x.property,
    nodePathAsLabel: x.node.nodePathAsLabel,
  }))
  const rootModel = modelSearcher.searchModel(currentSheetTree?.modelName)
  const properitesAndModelIdWithNode = addModelIdProperty(
    { node: currentSheetTree, model: rootModel },
    propertiesWithNode,
  )

  return (
    <Collapse defaultActiveKey={[]} className="mb-8">
      <Collapse.Panel header={t(`高度な設定`)} key="detail">
        <div>
          <Row justify="space-between" className="mb-4">
            <Col>
              <Space className="font-normal">
                <FilterOutlined />
                <span>{t(`一覧表示に適用されるフィルタ条件`)}</span>
              </Space>
            </Col>
            <Col>
              <Space>
                {t(`高度なフィルタ条件`)}
                <Switch
                  checked={isAdvancedModeState.isTrue}
                  onChange={(value) => {
                    if (value) {
                      isAdvancedModeState.setTrue()
                    } else {
                      isAdvancedModeState.setFalse()
                    }
                  }}
                />
              </Space>
            </Col>
          </Row>
          <ViewFilterTreeForm
            // treeの状態が変わったらfilterをリセットする（内部でuseStateしているので、こうしないとクリアされない）
            // KpiFormFilterTreeFormItemでは、filterFormItemCacheKeyもキャッシュキーに含めているが、
            // ここで変更するのはsheetExtraSettingであり、これは外部から変更されないので、ここで変更する必要はない
            cacheKey={stableStringify([currentSheetTree ?? ''])}
            filterTree={convertToFilterNodeForForm(filterTree ?? { logicalOperator: `and`, leafs: [], children: [] })}
            propertiesWithNode={properitesAndModelIdWithNode}
            isAdvancedMode={isAdvancedModeState.isTrue}
            inNestedForm
            onChange={(filterTree, option) => {
              const newFilterTree = applyAdvancedSettingsFlag(isAdvancedModeState.isTrue, filterTree)
              onChange(newFilterTree, option)
            }}
          />
        </div>
      </Collapse.Panel>
    </Collapse>
  )
}
